import { useState, useEffect } from 'react';

import Perfil from 'entity/Perfil';
import { usePerfil } from 'useAuth';
import { tr } from 'date-fns/locale';

/**
 * Hook para controlar permissões de cadastro de rodadas baseado no perfil do usuário
 */
export const useRodadaPermissions = () => {
    const perfil = usePerfil();
    const [permissions, setPermissions] = useState({
        canCreate: false,
        canEdit: false,
        isAdmin: false,
        isEmpresa: false,
        saldoRodadas: 0,
        dataFimContrato: null,
        reason: ''
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!perfil) {
            setLoading(false);
            setPermissions({
                canCreate: false,
                canEdit: false,
                isAdmin: false,
                isEmpresa: false,
                saldoRodadas: 0,
                dataFimContrato: null,
                reason: 'Perfil não encontrado'
            });
            return;
        }

        try {
            const isAdmin = perfil.tipoPerfil === 'admin';
            const isEmpresa = perfil.tipoPerfil === 'empresa';


            let canCreate = false;
            let canEdit = false;
            let reason = '';

            if (isAdmin) {
                console.log("isAdmin ", isAdmin);
                canCreate = true;
                canEdit = true;
                reason = 'Acesso total como administrador';
            } else if (isEmpresa) {
                // Verificar se o contrato está ativo (apenas se houver data definida)
                const now = new Date();
                const fimContrato = perfil.dataFimContrato ? new Date(perfil.dataFimContrato) : null;

                if (fimContrato && now > fimContrato) {
                    canCreate = false;
                    canEdit = false;
                    reason = 'Contrato expirado. Renove sua assinatura para continuar.';
                } else {
                    const saldo = perfil.saldoRodadas;
                    canEdit = true; // Pode editar rodadas existentes

                    // Verificar se o saldo é ilimitado
                    if (saldo === "" || saldo === null || saldo === undefined) {
                        canCreate = true;
                        const contratoInfo = fimContrato
                            ? `válido até ${fimContrato.toLocaleDateString('pt-BR')}`
                            : "sem limite de data";
                        reason = `Rodadas ilimitadas (${contratoInfo})`;
                    } else {
                        // Saldo numérico
                        const saldoNumerico = parseInt(saldo) || 0;
                        if (saldoNumerico > 0) {
                            canCreate = true;
                            reason = `${saldoNumerico} rodada(s) disponível(is)`;
                        } else {
                            canCreate = false;
                            reason = 'Saldo de rodadas esgotado. Entre em contato para adquirir mais créditos.';
                        }
                    }
                }
            } else if (perfil.tipoPerfil === 'colaborador') {
                canCreate = false;
                canEdit = true; // Colaboradores podem editar rodadas, mas não criar novas
                // reason = 'Apenas administradores e empresas podem cadastrar rodadas.';
            } else {
                canCreate = false;
                canEdit = false;
                reason = 'Apenas administradores e empresas podem cadastrar rodadas.';
            }

            setPermissions({
                canCreate,
                canEdit,
                isAdmin,
                isEmpresa,
                saldoRodadas: perfil.saldoRodadas || 0,
                dataFimContrato: perfil.dataFimContrato,
                reason
            });
        } catch (error) {
            console.error('Erro ao processar permissões:', error);
            setPermissions({
                canCreate: false,
                canEdit: false,
                isAdmin: false,
                isEmpresa: false,
                saldoRodadas: 0,
                dataFimContrato: null,
                reason: 'Erro ao verificar permissões'
            });
        } finally {
            setLoading(false);
        }
    }, [perfil]);    /**
     * Consome uma rodada do saldo do usuário (apenas para empresas)
     */
    const consumirRodada = async () => {
        console.log('consumirRodada')
        if (!perfil || perfil.tipoPerfil !== 'empresa') {
            return false;
        }

        try {
            const profileEntity = new Perfil();
            profileEntity.uid = perfil.uid;
            profileEntity.data = perfil;

            const success = await profileEntity.consumirRodada();

            if (success) {
                const saldoAtual = perfil.saldoRodadas;

                // Se saldo é ilimitado, não atualiza o estado
                if (saldoAtual === "" || saldoAtual === null || saldoAtual === undefined) {
                    return true; // Sucesso sem alterar saldo
                }

                // Se tem saldo numérico, atualiza as permissões
                const novoSaldo = (parseInt(saldoAtual) || 0) - 1;

                // Atualiza as permissões
                setPermissions(prev => ({
                    ...prev,
                    saldoRodadas: novoSaldo,
                    canCreate: novoSaldo > 0,
                    reason: novoSaldo > 0
                        ? `${novoSaldo} rodada(s) disponível(is)`
                        : 'Saldo de rodadas esgotado. Entre em contato para adquirir mais créditos.'
                }));
            }

            return success;
        } catch (error) {
            console.error('Erro ao consumir rodada:', error);
            return false;
        }
    };

    /**
     * Recarrega as permissões do usuário
     */
    const reloadPermissions = async () => {
        setLoading(true);
        // As permissões serão recarregadas automaticamente pelo useEffect quando o perfil mudar
        setLoading(false);
    };

    return {
        uidPerfil: perfil?.uid || null,
        userProfile: perfil,
        perfil,
        permissions,
        loading,
        consumirRodada,
        reloadPermissions
    };
};
